import Model from '@models/model.ts'
import { ALL_CHATBOT_CONVERSATIONS } from '@screens/Nora/graphql/allChatbotConversations.ts'
import { CHATBOT_CONVERSATION } from '@screens/Nora/graphql/chatbotConversation.ts'
import { UPDATE_CHATBOT_CONVERSATION } from '@screens/Nora/graphql/updateChatbotConversation.ts'
import { ALL_CHATBOT_QUICK_PROMPTS } from '@screens/Nora/graphql/allChatbotQuickPrompts.ts'
import { CREATE_CHATBOT_QUICK_PROMPT } from '@screens/Nora/graphql/createChatbotQuickPrompt.ts'
import { UPDATE_CHATBOT_QUICK_PROMPT } from '@screens/Nora/graphql/updateChatbotQuickPrompt.ts'
import { DELETE_CHATBOT_QUICK_PROMPT } from '@screens/Nora/graphql/deleteChatbotQuickPrompt.ts'
import { DELETE_CHATBOT_CONVERSATION } from '@screens/Nora/graphql/deleteChatbotConversation.ts'
import { ALL_HEALTH_DATA_REPORTS } from '@screens/Nora/graphql/allHealthDataReports.ts'
import { HEALTH_DATA_REPORT } from '@screens/Nora/graphql/healthDataReport.ts'
import { GENERATE_HEALTH_DATA_REPORT } from '@screens/Nora/graphql/generateHealthDataReport.ts'
import { GENERATE_HEALTH_DATA_REPORT_PDF } from '@screens/Nora/graphql/generateHealthDataReportPdf.ts'
import { UPDATE_HEALTH_DATA_REPORT_READ_AT } from '@screens/Nora/graphql/updateHealthDataReportReadAt.ts'
import { CHATBOT_SETTINGS } from '@screens/Nora/graphql/chatbotSettings.ts'
import { UPDATE_CHATBOT_SETTINGS } from '@screens/Nora/graphql/updateChatbotSettings.ts'
import { OPEN_CHATBOT_REPORT_CONVERSATION } from '@screens/Nora/graphql/openChatbotReportConversation.ts'
import { CHATBOT_REPORTS_ELIGIBLE } from '@screens/Nora/graphql/chatbotReportsEligible.ts'
import { CREATE_NORA_REALTIME_CLIENT_SECRET } from '@screens/Nora/graphql/createNoraRealtimeClientSecret.ts'
import { SYNTHESIZE_SPEECH } from '@screens/Nora/graphql/synthesizeSpeech.ts'
import { REPORT_CHATBOT_MESSAGE } from '@screens/Nora/graphql/reportChatbotMessage.ts'
import { TRANSCRIBE_UTTERANCE } from '@screens/Nora/graphql/transcribeUtterance.ts'
import reducers from './nora.reducers.ts'

export default class Nora {
  namespace = 'nora'

  state = {
    ...Model.defaultState,
    recentConversations: [],
    conversationsList: [],
    pinnedConversations: [],
    pinnedConversationsCount: 0,
    chatbotQuickPrompts: [],
    healthDataReports: [],
    chatbotReportsEligible: null,
    chatbotSettings: null,
  }

  effects = {
    allChatbotConversations: Model.buildEffect({
      name: `${this.namespace}/allChatbotConversations`,
      query: ALL_CHATBOT_CONVERSATIONS,
      caching: false,
      dataPath: 'allChatbotConversations',
      reducers: [{ name: 'setRecentConversations' }],
    }),
    loadConversationsList: Model.buildEffect({
      name: `${this.namespace}/loadConversationsList`,
      query: ALL_CHATBOT_CONVERSATIONS,
      caching: false,
      dataPath: 'allChatbotConversations',
      reducers: [{ name: 'setConversationsList' }],
    }),
    loadMoreConversationsList: Model.buildEffect({
      name: `${this.namespace}/loadMoreConversationsList`,
      query: ALL_CHATBOT_CONVERSATIONS,
      caching: false,
      dataPath: 'allChatbotConversations',
      reducers: [{ name: 'appendConversationsList' }],
    }),
    loadPinnedConversations: Model.buildEffect({
      name: `${this.namespace}/loadPinnedConversations`,
      query: ALL_CHATBOT_CONVERSATIONS,
      caching: false,
      dataPath: 'allChatbotConversations',
      reducers: [{ name: 'setPinnedConversations' }],
    }),
    chatbotConversation: Model.buildEffect({
      name: `${this.namespace}/chatbotConversation`,
      query: CHATBOT_CONVERSATION,
      caching: false,
      dataPath: 'chatbotConversation',
      reducers: [{ name: 'upsertChatbotConversation' }],
    }),
    updateChatbotConversation: Model.buildEffect({
      name: `${this.namespace}/updateChatbotConversation`,
      query: UPDATE_CHATBOT_CONVERSATION,
      caching: false,
      dataPath: 'updateChatbotConversation',
      reducers: [{ name: 'upsertChatbotConversation' }],
    }),
    deleteChatbotConversation: Model.buildEffect({
      name: `${this.namespace}/deleteChatbotConversation`,
      query: DELETE_CHATBOT_CONVERSATION,
      caching: false,
      dataPath: 'deleteChatbotConversation',
      reducers: [{ name: 'removeChatbotConversation' }],
    }),
    allChatbotQuickPrompts: Model.buildEffect({
      name: `${this.namespace}/allChatbotQuickPrompts`,
      query: ALL_CHATBOT_QUICK_PROMPTS,
      caching: false,
      dataPath: 'allChatbotQuickPrompts',
      reducers: [{ name: 'setChatbotQuickPrompts' }],
    }),
    createChatbotQuickPrompt: Model.buildEffect({
      name: `${this.namespace}/createChatbotQuickPrompt`,
      query: CREATE_CHATBOT_QUICK_PROMPT,
      caching: false,
      dataPath: 'createChatbotQuickPrompt',
      reducers: [{ name: 'insertChatbotQuickPrompt' }],
    }),
    updateChatbotQuickPrompt: Model.buildEffect({
      name: `${this.namespace}/updateChatbotQuickPrompt`,
      query: UPDATE_CHATBOT_QUICK_PROMPT,
      caching: false,
      dataPath: 'updateChatbotQuickPrompt',
      reducers: [{ name: 'updateChatbotQuickPrompts' }],
    }),
    deleteChatbotQuickPrompt: Model.buildEffect({
      name: `${this.namespace}/deleteChatbotQuickPrompt`,
      query: DELETE_CHATBOT_QUICK_PROMPT,
      caching: false,
      dataPath: 'deleteChatbotQuickPrompt',
      reducers: [{ name: 'removeChatbotQuickPrompt' }],
    }),
    allHealthDataReports: Model.buildEffect({
      name: `${this.namespace}/allHealthDataReports`,
      query: ALL_HEALTH_DATA_REPORTS,
      caching: false,
      dataPath: 'allHealthDataReports',
      reducers: [{ name: 'setHealthDataReports' }],
    }),
    healthDataReport: Model.buildEffect({
      name: `${this.namespace}/healthDataReport`,
      query: HEALTH_DATA_REPORT,
      caching: false,
      dataPath: 'healthDataReport',
      reducers: [{ name: 'updateHealthDataReport' }],
    }),
    generateHealthDataReport: Model.buildEffect({
      name: `${this.namespace}/generateHealthDataReport`,
      query: GENERATE_HEALTH_DATA_REPORT,
      caching: false,
      dataPath: 'generateHealthDataReport',
      reducers: [{ name: 'insertHealthDataReport' }],
    }),
    generateHealthDataReportPdf: Model.buildEffect({
      name: `${this.namespace}/generateHealthDataReportPdf`,
      query: GENERATE_HEALTH_DATA_REPORT_PDF,
      caching: false,
      dataPath: 'generateHealthDataReportPdf',
    }),
    markHealthDataReportRead: Model.buildEffect({
      name: `${this.namespace}/markHealthDataReportRead`,
      query: UPDATE_HEALTH_DATA_REPORT_READ_AT,
      caching: false,
      dataPath: 'updateHealthDataReportReadAt',
      reducers: [{ name: 'updateHealthDataReport' }],
    }),
    chatbotSettings: Model.buildEffect({
      name: `${this.namespace}/chatbotSettings`,
      query: CHATBOT_SETTINGS,
      caching: false,
      dataPath: 'chatbotSettings',
      reducers: [{ name: 'setChatbotSettings' }],
    }),
    updateChatbotSettings: Model.buildEffect({
      name: `${this.namespace}/updateChatbotSettings`,
      query: UPDATE_CHATBOT_SETTINGS,
      caching: false,
      dataPath: 'updateChatbotSettings',
      reducers: [{ name: 'setChatbotSettings' }],
    }),
    openChatbotReportConversation: Model.buildEffect({
      name: `${this.namespace}/openChatbotReportConversation`,
      query: OPEN_CHATBOT_REPORT_CONVERSATION,
      caching: false,
      dataPath: 'openChatbotReportConversation',
      reducers: [{ name: 'upsertChatbotConversation' }],
    }),
    fetchChatbotReportsEligible: Model.buildEffect({
      name: `${this.namespace}/fetchChatbotReportsEligible`,
      query: CHATBOT_REPORTS_ELIGIBLE,
      caching: false,
      dataPath: 'uiFlags',
      reducers: [{ name: 'setChatbotReportsEligible' }],
    }),
    createNoraRealtimeClientSecret: Model.buildEffect({
      name: `${this.namespace}/createNoraRealtimeClientSecret`,
      query: CREATE_NORA_REALTIME_CLIENT_SECRET,
      caching: false,
      dataPath: 'createNoraRealtimeClientSecret',
    }),
    transcribeUtterance: Model.buildEffect({
      name: `${this.namespace}/transcribeUtterance`,
      query: TRANSCRIBE_UTTERANCE,
      caching: false,
      dataPath: 'transcribeUtterance',
    }),
    synthesizeSpeech: Model.buildEffect({
      name: `${this.namespace}/synthesizeSpeech`,
      query: SYNTHESIZE_SPEECH,
      caching: false,
      dataPath: 'synthesizeSpeech',
    }),
    reportChatbotMessage: Model.buildEffect({
      name: `${this.namespace}/reportChatbotMessage`,
      query: REPORT_CHATBOT_MESSAGE,
      caching: false,
      dataPath: 'reportChatbotMessage',
    }),
  }

  reducers = reducers
}
